import { PatientPharmacyEditDrawer } from '@app/icare/entities/patient/edits/PatientPharmacyEditDrawer'
import { patientFindingsQueryOptions } from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import type { PatientPharmacyRelation } from '@app/icare/entities/patient/schema/PatientPharmacy'
import { PharmacyConfig } from '@app/icare/entities/pharmacy/config'
import { patientPharmacyQueries } from '@app/icare/entities/pharmacy/queries/patientPharmacyQueries'
import { RelationTabs } from '@app/icare/routes/_app/patienten/$id/_edit/-components/RelationTabs'
import { useRelationCommitNavigation } from '@app/icare/routes/_app/patienten/$id/_edit/-components/useRelationCommitNavigation'
import { isActivePatientRelation } from '@app/icare/shared/api'
import { t } from '@lingui/core/macro'
import { entityParams } from '@pkg/db/utils'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_pharmacy/apotheken')({
  params: entityParams({ id: PatientId }),
  loader: ({ params, context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(patientPharmacyQueries.forTabs(params.id)),
      queryClient.ensureQueryData(patientFindingsQueryOptions(params.id)),
    ]),
  component: RouteComponent,
})

function RouteComponent() {
  const { id } = Route.useParams()
  const { data: relations = [] } = useSuspenseQuery(patientPharmacyQueries.forTabs(id))
  const { data: findings = [] } = useQuery(patientFindingsQueryOptions(id))
  const { data: totalCount } = useQuery(patientPharmacyQueries.totalCount(id))
  const navigate = Route.useNavigate()

  const handleAfterCommit = useRelationCommitNavigation<PatientPharmacyRelation>({
    isInactive: relation => !isActivePatientRelation(relation),
    onCreateNavigate: relation =>
      navigate({
        to: '/patienten/$id/apotheken/$relation',
        params: { id, relation: relation.id },
      }),
    onInactiveEditNavigate: () => navigate({ to: '.', replace: true }),
  })

  return (
    <RelationTabs
      relations={relations}
      relationTotalCount={totalCount}
      basePath={`/patienten/${id}/apotheken`}
      findings={findings}
      findingsGroup="pharmacies"
      getLabel={item => PharmacyConfig.format.primary(item.to) ?? t`Apotheke`}
      onAdd={params =>
        void PatientPharmacyEditDrawer.show({
          patientId: id,
          onAfterCommit: handleAfterCommit,
          ...params,
        })
      }
      entity="pharmacy"
    />
  )
}
